<script setup lang="ts">
/**
 * Text hinzufügen (Überschrift / Untertitel / Fließtext) und Schrift wählen.
 * Ist ein Text ausgewählt, ändert ein Klick auf eine Schrift dessen Schrift – sonst entsteht ein neuer Text.
 */
import { computed, onMounted, watch } from 'vue'
import { Heading1, Heading2, Pilcrow } from 'lucide-vue-next'
import type { TextLayer } from '@pfe/design-renderer'
import type { DesignFontSummary } from '../../../services/designApi'
import { useDesignStore } from '../../../store/useDesignStore'
import { ensureFontsLoaded } from '../../../design/fonts/fontLoader'

const props = defineProps<{ fonts: DesignFontSummary[] }>()
const store = useDesignStore()

const selectedText = computed(() => (store.selectedLayer?.type === 'text' ? (store.selectedLayer as TextLayer) : null))
const canvasH = computed(() => store.activeDoc?.canvas.heightMm ?? 100)

const PRESETS = [
  { key: 'h1', label: 'Überschrift', icon: Heading1, text: 'Überschrift', size: () => Math.max(18, Math.round(canvasH.value / 4.5)), weight: 700, h: 0.18 },
  { key: 'h2', label: 'Untertitel', icon: Heading2, text: 'Untertitel', size: () => Math.max(12, Math.round(canvasH.value / 8)), weight: 600, h: 0.12 },
  { key: 'p', label: 'Fließtext', icon: Pilcrow, text: 'Hier steht dein Text', size: () => Math.max(8, Math.round(canvasH.value / 14)), weight: 400, h: 0.1 },
]

function weightFor(f: DesignFontSummary | undefined, wanted: number) {
  const ws = (f?.faces ?? []).filter((x) => !x.italic).map((x) => x.weight)
  if (!ws.length) return wanted
  return ws.reduce((best: number, w: number) => (Math.abs(w - wanted) < Math.abs(best - wanted) ? w : best), ws[0] ?? wanted)
}

function addPreset(p: (typeof PRESETS)[number]) {
  const f = props.fonts.find((x) => x.id === selectedText.value?.font.familyId) ?? props.fonts[0]
  store.addTextLayer(p.text, f?.id ?? '', f?.family, { sizePt: p.size(), weight: weightFor(f, p.weight), heightFactor: p.h })
  store.editingTextId = store.selection[0] ?? null // sofort lostippen
}

function pickFont(f: DesignFontSummary) {
  const t = selectedText.value
  if (t) {
    const { fittedSizePt: _drop, ...rest } = t as any
    store.beginTxn()
    store.patchLayer(t.id, { ...rest, font: { ...t.font, familyId: f.id, family: f.family, weight: weightFor(f, t.font.weight) } } as any)
    store.commitTxn()
    return
  }
  store.addTextLayer('Dein Text', f.id, f.family, { weight: weightFor(f, 700) })
  store.editingTextId = store.selection[0] ?? null
}

// Vorschau: alle Schriften laden, damit die Liste in der jeweiligen Schrift erscheint
const loadAll = () => ensureFontsLoaded(props.fonts, props.fonts.map((f) => f.id)).catch(() => {})
onMounted(loadAll)
watch(() => props.fonts.map((f) => f.id).join(','), loadAll)

const CATEGORY_LABEL: Record<string, string> = { sans: 'Serifenlos', serif: 'Serif', script: 'Schreibschrift', handwriting: 'Handschrift', display: 'Plakativ', mono: 'Monospace' }
</script>

<template>
  <div class="tp">
    <div class="tp__presets">
      <button v-for="p in PRESETS" :key="p.key" type="button" class="tp__preset" :class="`tp__preset--${p.key}`" :disabled="!fonts.length" @click="addPreset(p)">
        <component :is="p.icon" :size="18" aria-hidden="true" />
        <span>{{ p.label }}</span>
      </button>
    </div>
    <p v-if="!fonts.length" class="tp__hint">Für dieses Produkt sind noch keine Schriften freigegeben.</p>
    <p v-else class="tp__hint">
      Neuer Text ist sofort markiert – einfach lostippen. Doppelklick auf einen Text bearbeitet ihn direkt auf der Fläche, <kbd>Esc</kbd> beendet die Eingabe.
    </p>

    <section v-if="fonts.length" class="tp__fonts" aria-label="Schriften">
      <h3 class="tp__title">{{ selectedText ? 'Schrift für den ausgewählten Text' : 'Text mit Schrift hinzufügen' }}</h3>
      <button
        v-for="f in fonts"
        :key="f.id"
        type="button"
        class="tp__font"
        :class="{ 'tp__font--on': selectedText?.font.familyId === f.id }"
        :aria-pressed="selectedText?.font.familyId === f.id"
        @click="pickFont(f)"
      >
        <span class="tp__font-sample" :style="{ fontFamily: `'${f.family}', system-ui, sans-serif` }">{{ f.name }}</span>
        <span class="tp__font-meta">{{ CATEGORY_LABEL[f.category] || f.category }}</span>
      </button>
    </section>
  </div>
</template>

<style scoped>
.tp { display: grid; gap: 12px; padding: 14px 16px; }
.tp__presets { display: grid; gap: 6px; }
.tp__preset { display: flex; align-items: center; gap: 10px; width: 100%; padding: 10px 12px; border: 1px solid rgba(15, 23, 42, 0.14); border-radius: 8px; background: #fff; color: #0f172a; font: inherit; cursor: pointer; text-align: left; }
.tp__preset:hover:not(:disabled) { border-color: rgba(15, 23, 42, 0.32); }
.tp__preset:disabled { opacity: 0.5; cursor: default; }
.tp__preset svg { flex: none; color: rgba(15, 23, 42, 0.55); }
.tp__preset--h1 span { font-size: 17px; font-weight: 700; }
.tp__preset--h2 span { font-size: 14px; font-weight: 600; }
.tp__preset--p span { font-size: 13px; }
.tp__hint { margin: 0; font-size: 12px; line-height: 1.45; color: rgba(15, 23, 42, 0.62); }
.tp__hint kbd { padding: 0 4px; border: 1px solid rgba(15, 23, 42, 0.2); border-radius: 3px; font: inherit; font-size: 11px; }
.tp__fonts { display: grid; gap: 4px; }
.tp__title { margin: 4px 0 2px; font-size: 12px; font-weight: 700; color: rgba(15, 23, 42, 0.7); text-transform: uppercase; letter-spacing: 0.03em; }
.tp__font { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; width: 100%; padding: 8px 10px; border: 1px solid transparent; border-radius: 6px; background: transparent; color: #0f172a; font: inherit; cursor: pointer; text-align: left; }
.tp__font:hover { background: rgba(15, 23, 42, 0.04); }
.tp__font:focus-visible { outline: 2px solid var(--pfe-color-accent, #e30b17); outline-offset: 1px; }
.tp__font--on { border-color: var(--pfe-color-accent, #e30b17); background: rgba(227, 11, 23, 0.06); }
.tp__font-sample { min-width: 0; font-size: 19px; line-height: 1.2; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.tp__font-meta { flex: none; font-size: 11px; color: rgba(15, 23, 42, 0.5); }
</style>
